import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import FormHelperText from '@mui/material/FormHelperText';
import {
  Alert,
  Box,
  Button,
  DialogActions,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  Modal,
  Radio,
  RadioGroup,
  Tooltip,
  Typography
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { getFormattedUserNameWithId } from '../../shared/UserLogin';
import SupplierDropDownSearch from '../SupplierDropDownSearch';
import {
  NON_STRATEGIC,
  SDIS_API_ERR_MSG,
  SRO_SBM_PROP_NAME,
  SRO_SBM_USER_ID_PROP_NAME,
  STRATEGIC,
  SUPPLIER_STATUS_CONTROL,
  SUPPLIER_STATUS_DONOTUSE,
  SUPPLIER_STATUS_GROW,
  SUPPLIER_STATUS_LIMITED_USE,
  SUPPLIER_STATUS_MAINTAIN,
  SUPPLIER_STATUS_REDUCE
} from '../../shared/AppConstants';
import {
  findSupplier,
  isCommentsEmptyOrSame
} from '../../shared/UtilFunctions';
import {
  StrategicTooltipContent,
  NonStrategicTooltipContent
} from '../../shared/SupplierStatusTooltip';
import SMModal from '../../shared/SMModal';
import TextFieldComponent from '../../shared/TextFieldComponent';
import { getSdiMap } from '../../shared/APIHelper';

// Non-Strategic to Strategic move
export default function SupplierPopup({
  handleCancel,
  readOnly,
  setStatusErrorMsg,
  showModal,
  dispatchSupplier,
  strategyInformation,
  setSuppliersMessage,
  hasDuplicateSuppliers,
  supplier,
  userInfoMap,
  dispatchNonStrategicSupplier,
  isNonStrategicSupplier,
  setNonStrategicSuppliers,
  nonSupplier,
  setNonSupplier,
  enforceCommentsWhenSupplierTypeChanged,
  commonProps
}) {
  const [showDirtyModal, setShowDirtyModal] = useState(false);
  const handleDirtyModalCancel = () => setShowDirtyModal(false);

  const [editMode, setEditMode] = useState(false);
  const [supplierTypeChanged, setSupplierTypeChanged] = useState(false);
  const [newSupplierType, setNewSupplierType] = useState(null);
  const [showCommentsWarning, setShowCommentsWarning] = useState(null);
  const [commentAlertType, setCommentAlertType] = useState(null);
  const actualSupplierType =
    supplier &&
    (supplier.status === SUPPLIER_STATUS_REDUCE ||
      supplier.status === SUPPLIER_STATUS_DONOTUSE)
      ? NON_STRATEGIC
      : STRATEGIC;

  const {
    getValues,
    reset,
    control,
    register,
    formState,
    setError,
    clearErrors
  } = useForm({
    mode: 'all'
  });
  const { isDirty, errors } = formState;

  function isFormDirty() {
    return isDirty;
  }

  function cancelAction() {
    if (isFormDirty()) setShowDirtyModal(true);
    else {
      clearErrors();
      handleCancel();
    }
  }

  const cancelDirtyOKAction = () => {
    setShowDirtyModal(false);
    clearErrors();
    handleCancel();
  };

  const addSuppliers = async () => {
    let supplierLabel = [];
    let tmpSupplierLabel;
    let suppliers = getValues('suppliers');

    if (!suppliers && !editMode) {
      setError('suppliers', {
        message: 'Please select at least one Supplier',
        type: 'manual'
      });
      return;
    }
    if (editMode)
      suppliers = {
        label: supplier.supplierNumber + ' - ' + supplier.supplierName,
        value: supplier.supplierNumber
      };

    const [sdis] = await Promise.allSettled([getSdiMap(suppliers.value)]);

    if (sdis.status === 'rejected') {
      commonProps.setStatusErrorMsg(SDIS_API_ERR_MSG);
    } else {
      suppliers.sdis = sdis.value.get(suppliers.value);
    }

    let status = getValues('status');

    if (!status) {
      setError('status', {
        message: 'Status is required',
        type: 'manual'
      });
      return;
    }

    let comments = getValues('comments');
    if (supplierTypeChanged && isCommentsEmptyOrSame(comments, supplier)) {
      setCommentAlertType('danger');
      return;
    }

    // Remove Supplier Number from Name
    if (suppliers) {
      let name = suppliers.label;
      let suppNumber = suppliers.value;

      name = name.replace(suppNumber + ' - ', '');

      if (newSupplierType === STRATEGIC) {
        tmpSupplierLabel = {
          supplierNumber: suppNumber,
          supplierName: name,
          status: status,
          sdis: suppliers.sdis,
          comments: comments,
          riskRating: supplier?.riskRating,
          aeRating: supplier?.aeRating,
          activeSupplier: supplier?.activeSupplier,
          forecastSpend: supplier?.forecastSpend,
          invoiceSpend: supplier?.invoiceSpend,
          forecastSpendPercentage: supplier?.forecastSpendPercentage,
          mapInvoiceSpend: supplier?.mapInvoiceSpend,
          mapForecastSpend: supplier?.mapForecastSpend,
          sroSbmName: supplier?.[SRO_SBM_PROP_NAME],
          sroSbmUserId: supplier?.[SRO_SBM_USER_ID_PROP_NAME]
        };
      }

      supplierLabel.push({
        supplierNumber: suppNumber,
        supplierName: name,
        sdis: suppliers.sdis,
        statusDesc: status,
        status: status,
        comments: comments,
        forecastSpend: supplier?.forecastSpend,
        invoiceSpend: supplier?.invoiceSpend,
        forecastSpendPercentage: supplier?.forecastSpendPercentage,
        mapInvoiceSpend: supplier?.mapInvoiceSpend,
        mapForecastSpend: supplier?.mapForecastSpend
      });
    }
    //Add Supplier Duplicate Supplier check
    if (!editMode && hasDuplicateSuppliers(supplierLabel)) {
      setError('suppliers', {
        message: 'Supplier already exists on this strategy.',
        type: 'manual'
      });
      return;
    }

    //Non-Strategic Supplier payload for explicit persist
    if (isNonStrategicSupplier) {
      let newArray = [];

      if (
        (status === SUPPLIER_STATUS_REDUCE && comments !== '') ||
        status === SUPPLIER_STATUS_DONOTUSE ||
        status === SUPPLIER_STATUS_LIMITED_USE
      ) {
        if (nonSupplier && nonSupplier.length > 0) {
          newArray = nonSupplier.map((elem) => {
            if (elem.supplierNumber === supplier.supplierNumber)
              return supplierLabel;
            else return elem;
          });
          if (!newArray.includes(supplierLabel)) newArray.push(supplierLabel);
        } else newArray = supplierLabel;
      } else if (nonSupplier && nonSupplier.length > 0) {
        //If Status changed from Non-Strategic to Strategic remove from Non-strategic Supplier
        newArray = nonSupplier.filter((record) => {
          return record.supplierNumber !== supplier.supplierNumber;
        });
      }
      setNonSupplier(newArray.flat());
      setNonStrategicSuppliers(newArray.flat());
    }

    if (editMode) {
      if (isNonStrategicSupplier) {
        if (
          status !== SUPPLIER_STATUS_REDUCE &&
          status !== SUPPLIER_STATUS_DONOTUSE &&
          status !== SUPPLIER_STATUS_LIMITED_USE
        ) {
          if (newSupplierType === STRATEGIC)
            dispatchSupplier({ type: 'add', payload: tmpSupplierLabel });
          else dispatchSupplier({ type: 'add', payload: supplierLabel });
          dispatchNonStrategicSupplier({
            type: 'del',
            payload: supplier
          });
        } else
          dispatchNonStrategicSupplier({
            type: 'edit',
            payload: supplierLabel
          });
      } else if (
        status === SUPPLIER_STATUS_REDUCE ||
        status === SUPPLIER_STATUS_DONOTUSE ||
        status === SUPPLIER_STATUS_LIMITED_USE
      ) {
        dispatchNonStrategicSupplier({
          type: 'add',
          payload: supplierLabel
        });
        dispatchSupplier({ type: 'del', payload: supplier });

        if (nonSupplier && nonSupplier.length > 0)
          Array.prototype.push.apply(nonSupplier, supplierLabel);
        else nonSupplier = supplierLabel;
        setNonSupplier(nonSupplier);
        setNonStrategicSuppliers(nonSupplier);
      } else
        dispatchSupplier({
          type: 'edit',
          payload: supplierLabel
        });
    } else {
      // Add Mode
      let strategicSupplierToAdd = supplierLabel[0];
      let foundSplr = findSupplier(
        strategyInformation,
        strategicSupplierToAdd.supplierNumber
      );

      if (foundSplr) {
        // Found, delete from Non-Strategic side
        dispatchNonStrategicSupplier({
          type: 'del',
          payload: foundSplr
        });

        // Copy Spend Info to strategicSupplierToAdd
        strategicSupplierToAdd.invoiceSpend = foundSplr.invoiceSpend;
        strategicSupplierToAdd.forecastSpend = foundSplr.forecastSpend;
        strategicSupplierToAdd.forecastSpendPercentage =
          foundSplr.forecastSpendPercentage;
      }

      dispatchSupplier({
        type: 'add',
        payload: supplierLabel
      });

      setNonSupplier(nonSupplier);
      setNonStrategicSuppliers(nonSupplier);
    }

    setSuppliersMessage &&
      setSuppliersMessage(
        'Changes to supplier will not take effect until strategy is saved.'
      );
    handleCancel();
  };
  useEffect(() => {
    if (supplier) {
      reset(
        {
          comments: supplier.comments ? supplier.comments : '',
          status: supplier.status || supplier.statusDesc || ''
        },
        {
          keepErrors: false,
          keepDirty: false,
          keepIsSubmitted: false,
          keepTouched: false,
          keepIsValid: false,
          keepSubmitCount: false
        }
      );
      setEditMode(true);
    }
  }, [supplier]);

  useEffect(() => {
    if (enforceCommentsWhenSupplierTypeChanged) {
      if (
        supplier &&
        actualSupplierType &&
        newSupplierType &&
        actualSupplierType !== newSupplierType
      ) {
        let comments = getValues('comments');

        setShowCommentsWarning(isCommentsEmptyOrSame(comments, supplier));
        setSupplierTypeChanged(true);
        setCommentAlertType('warning');
      } else {
        setShowCommentsWarning(false);
        setSupplierTypeChanged(false);
        setCommentAlertType(null);
      }
    }
  }, [newSupplierType, setNewSupplierType]);

  function paintEnforceCommentsWhenSupplierTypeChanged() {
    return (
      true === supplierTypeChanged &&
      true === showCommentsWarning &&
      commentAlertType && (
        <Box>
          <Alert variant={commentAlertType}>
            <h5>Updated Comment Required.</h5>
            {newSupplierType && newSupplierType === NON_STRATEGIC ? (
              <h6>Status Changed to Non-Strategic.</h6>
            ) : (
              <h6>Status Changed to Strategic.</h6>
            )}
          </Alert>
        </Box>
      )
    );
  }

  return (
    <>
      <Modal open={showModal} onClose={cancelAction}>
        <Box
          sx={(theme) => ({
            width: 'min(44rem, 92vw)',
            maxHeight: '88vh',
            overflowY: 'auto',
            mx: 'auto',
            mt: '10vh',
            bgcolor: 'background.paper',
            borderRadius: theme.fuel6Tokens.JddcRadiusSmall,
            boxShadow: theme.shadows[24],
            p: theme.fuel6Tokens.JddcSpacingMedium
          })}
        >
          <DialogTitle sx={{ p: 0 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                mb: 2
              }}
            >
              <Box>
                <Typography variant="h2" sx={{ mb: editMode ? 0.5 : 0 }}>
                  {editMode ? 'Edit Supplier' : 'Add Supplier'}
                </Typography>
                {editMode && (
                  <>
                    <Typography variant="bodySmall" color="text.primary">
                      {supplier &&
                        supplier.supplierNumber + ' - ' + supplier.supplierName}
                    </Typography>
                    {supplier && supplier.updatedById && (
                      <Typography variant="bodySmall" color="text.secondary">
                        Last updated on{' '}
                        {dayjs(supplier.updatedOn).format('DD-MMM-YYYY')} by{' '}
                        {getFormattedUserNameWithId(
                          userInfoMap && userInfoMap.get(supplier.updatedById),
                          supplier.updatedById
                        )}
                      </Typography>
                    )}
                  </>
                )}
              </Box>
              <IconButton
                aria-label="close"
                onClick={cancelAction}
                size="small"
              >
                <fuel-icon name="close" />
              </IconButton>
            </Box>
          </DialogTitle>
          <Box sx={{ p: 0 }}>
            <Box component="form">
              {(!editMode || (supplier && supplier.updatedById)) && (
                <Box
                  sx={(theme) => ({ mb: theme.fuel6Tokens.JddcSpacingLarge })}
                >
                  {!editMode ? (
                    <>
                      <Box>
                        <label style={{ display: 'block' }}>
                          <Typography component="span" variant="labelMedium">
                            Supplier
                          </Typography>
                          <Typography
                            component="span"
                            variant="labelMedium"
                            sx={{ color: 'error.main', ml: 0.5 }}
                          >
                            *
                          </Typography>
                        </label>

                        <Box sx={{ mt: 1 }}>
                          <SupplierDropDownSearch
                            readOnly={readOnly}
                            control={control}
                            name="suppliers"
                            showSingleOption={true}
                            setStatusErrorMsg={setStatusErrorMsg}
                            placeholderText="Search"
                          />
                        </Box>
                      </Box>
                      {errors.suppliers && (
                        <Box>
                          <FormHelperText style={{ fontSize: '18px' }}>
                            {errors.suppliers.message}
                          </FormHelperText>
                        </Box>
                      )}
                    </>
                  ) : null}
                </Box>
              )}
              {paintEnforceCommentsWhenSupplierTypeChanged()}
              <Box>
                <Box>
                  <label>
                    <Typography component="span" variant="labelMedium">
                      Status
                    </Typography>
                    <Typography
                      component="span"
                      variant="labelMedium"
                      sx={{ color: 'error.main', ml: 0.5 }}
                    >
                      *
                    </Typography>
                  </label>

                  <Box
                    sx={{
                      mt: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2
                    }}
                  >
                    <Controller
                      name="status"
                      control={control}
                      defaultValue={
                        supplier?.status || supplier?.statusDesc || ''
                      }
                      render={({ field }) => (
                        <RadioGroup
                          aria-label="Supplier status"
                          name={field.name}
                          value={field.value || ''}
                          onChange={(event) => {
                            const selectedStatus = event.target.value;

                            field.onChange(selectedStatus);
                            setNewSupplierType(
                              [
                                SUPPLIER_STATUS_GROW,
                                SUPPLIER_STATUS_MAINTAIN,
                                SUPPLIER_STATUS_CONTROL
                              ].includes(selectedStatus)
                                ? STRATEGIC
                                : NON_STRATEGIC
                            );
                          }}
                        >
                          <FormControl sx={{ mb: 2 }}>
                            <Typography
                              variant="labelMedium"
                              sx={{ color: 'text.secondary', mb: 1 }}
                            >
                              Strategic{'  '}
                              <Tooltip
                                title={<StrategicTooltipContent />}
                                placement="top-start"
                              >
                                <Box
                                  component="span"
                                  style={{
                                    fontSize: '1rem',
                                    display: 'inline-flex'
                                  }}
                                >
                                  <fuel-icon name="info_outlined" />
                                </Box>
                              </Tooltip>
                            </Typography>
                            <Box
                              sx={{
                                display: 'flex',
                                gap: 2,
                                flexWrap: 'wrap'
                              }}
                            >
                              <FormControlLabel
                                value={SUPPLIER_STATUS_GROW}
                                control={<Radio size="small" />}
                                label={SUPPLIER_STATUS_GROW}
                              />
                              <FormControlLabel
                                value={SUPPLIER_STATUS_MAINTAIN}
                                control={<Radio size="small" />}
                                label={SUPPLIER_STATUS_MAINTAIN}
                              />
                              <FormControlLabel
                                value={SUPPLIER_STATUS_CONTROL}
                                control={<Radio size="small" />}
                                label={SUPPLIER_STATUS_CONTROL}
                              />
                            </Box>
                          </FormControl>

                          {editMode && (
                            <FormControl>
                              <Typography
                                variant="labelMedium"
                                sx={{ color: 'text.secondary', mb: 1 }}
                              >
                                Non-Strategic{'  '}
                                <Tooltip title={<NonStrategicTooltipContent />}>
                                  <Box
                                    component="span"
                                    style={{
                                      fontSize: '1rem',
                                      display: 'inline-flex'
                                    }}
                                  >
                                    <fuel-icon name="info_outlined" />
                                  </Box>
                                </Tooltip>
                              </Typography>
                              <Box
                                sx={{
                                  display: 'flex',
                                  gap: 2,
                                  flexWrap: 'wrap'
                                }}
                              >
                                <FormControlLabel
                                  value={SUPPLIER_STATUS_LIMITED_USE}
                                  control={<Radio size="small" />}
                                  label={SUPPLIER_STATUS_LIMITED_USE}
                                />
                                <FormControlLabel
                                  value={SUPPLIER_STATUS_REDUCE}
                                  control={<Radio size="small" />}
                                  label={SUPPLIER_STATUS_REDUCE}
                                />
                                <FormControlLabel
                                  value={SUPPLIER_STATUS_DONOTUSE}
                                  control={<Radio size="small" />}
                                  label={SUPPLIER_STATUS_DONOTUSE}
                                />
                              </Box>
                            </FormControl>
                          )}
                        </RadioGroup>
                      )}
                    />
                  </Box>
                </Box>
              </Box>
              {errors.status && (
                <FormHelperText style={{ fontSize: '18px' }}>
                  {errors.status.message}
                </FormHelperText>
              )}
            </Box>
            <Box>
              <label>
                <Typography component="span" variant="labelMedium">
                  Comments
                </Typography>
                {supplierTypeChanged && true === supplierTypeChanged && (
                  <Typography
                    component="span"
                    variant="labelMedium"
                    sx={{ color: 'error.main', ml: 0.5 }}
                  >
                    *
                  </Typography>
                )}
              </label>
              <Box sx={{ mt: 1 }}>
                <TextFieldComponent
                  maxLength="512"
                  minRows={3}
                  name="comments"
                  control={control}
                  placeholder="Enter comments..."
                  isError={
                    supplierTypeChanged &&
                    true === supplierTypeChanged &&
                    commentAlertType === 'danger'
                  }
                  defaultValue={supplier && supplier.comments}
                />
              </Box>
            </Box>
          </Box>
        </Box>
        <DialogActions sx={{ p: 0, pt: 3, gap: 1.5 }}>
          <Button fullWidth variant="secondary" onClick={cancelAction}>
            Cancel
          </Button>

          <Button fullWidth variant="primary" onClick={addSuppliers}>
            {editMode ? 'Update Supplier' : 'Add Supplier'}
          </Button>
        </DialogActions>
      </Modal>
      <SMModal
        showModal={showDirtyModal}
        handleCancel={handleDirtyModalCancel}
        handleYes={cancelDirtyOKAction}
        title="Changes Will Be Lost"
        msg_line1="Do you want to save your changes before leaving this screen?"
        msg_line2="If you don't save, your changes will be lost."
        cancelText="Cancel"
        yesText="Continue without Saving"
      />
    </>
  );
}
